// ============================================================
// CONVERSATION PERSISTENCE (localStorage per mode)
// ============================================================
//
// Lifted out of MediaChatBubble.tsx unchanged. It lives on its own because it
// is the one part of that component with behaviour worth testing directly —
// the scope keying below was a real defect — and until now it could not be
// reached without mounting the whole chat.

import type { ChatMessage, ChatMode } from '../../services/mediaChatService';

/** Turns of history sent per request. */
export const HISTORY_TURNS = 20;

/**
 * Messages kept in localStorage.
 *
 * ONE constant for both directions on purpose: save trims to this and load
 * reads back to it, so if the two ever disagreed the transcript would be
 * silently asymmetric — you would persist more than you could ever read, or
 * read a window the writer had already cut.
 */
export const PERSISTED_MESSAGES = 20;

const STORAGE_KEY_PREFIX = 'mi-chat-history';

/**
 * Keyed by mode AND scope.
 *
 * It was keyed by mode alone, which was fine while one page owned each mode.
 * Two pages in 'performance' mode share the transcript but NOT the context —
 * /campaigns carries trends, brokers and MMM status that the dashboard does
 * not — so a follow-up asked on one page would be answered against the other
 * page's data, with the earlier numbers still sitting in the history as though
 * they were grounded.
 *
 * `scope` defaults to undefined so every existing page keeps the history it
 * already has.
 */
export const getStorageKey = (mode: ChatMode, scope?: string): string =>
  scope ? `${STORAGE_KEY_PREFIX}-${mode}-${scope}` : `${STORAGE_KEY_PREFIX}-${mode}`;

export const loadMessages = (mode: ChatMode, scope?: string): ChatMessage[] => {
  try {
    const stored = localStorage.getItem(getStorageKey(mode, scope));
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.slice(-PERSISTED_MESSAGES) : [];
  } catch {
    return [];
  }
};

export const saveMessages = (mode: ChatMode, messages: ChatMessage[], scope?: string): void => {
  try {
    const trimmed = messages.slice(-PERSISTED_MESSAGES);
    localStorage.setItem(getStorageKey(mode, scope), JSON.stringify(trimmed));
  } catch {
    // localStorage might be full or unavailable — non-critical
  }
};

export const clearMessages = (mode: ChatMode, scope?: string): void => {
  try {
    localStorage.removeItem(getStorageKey(mode, scope));
  } catch {
    // non-critical
  }
};
